import type { Middleware } from 'openapi-fetch';
import { persistPendingRequest } from './federated-step-up';

/**
 * CustomEvent dispatched on `window` when a federated (SSO) step-up is
 * required. A FederatedStepUpProvider listens for this, shows a confirmation
 * prompt, and on confirm navigates to the IdP re-auth initiate endpoint.
 */
export const STEP_UP_FEDERATED_EVENT = 'sentinel:step-up-federated-required';

/**
 * Minimal queue contract the middleware needs. Matches the shape exported
 * from `@sentinel/auth-core` (`StepUpQueue`). We accept the contract here
 * rather than importing from auth-core to keep dependency direction clean
 * (auth-core depends on api-client, not the reverse).
 */
export interface StepUpQueueLike {
  enqueue(payload: { acr: string }): { id: string; promise: Promise<void> };
}

export interface StepUpMiddlewareOptions {
  queue: StepUpQueueLike;
  /** Override window for tests / non-browser environments. */
  win?: Pick<Window, 'dispatchEvent'> & {
    location?: { pathname: string; search?: string };
    CustomEvent: typeof CustomEvent;
  };
  /**
   * Re-issue the cloned Request through the full client chain (so CSRF and
   * any other middlewares are re-applied against the freshly rotated session).
   * Preferred over `fetcher` when supplied.
   */
  replay?: (request: Request) => Promise<Response>;
  /** Fallback fetcher when `replay` is not supplied (test-only / advanced). */
  fetcher?: typeof fetch;
  /**
   * Returns true when the current user authenticates exclusively via an
   * external IdP (`local_auth_managed_by_idp`). Defense-in-depth: if the
   * backend ever returns method:'local' for such a user, we still full-page
   * redirect to the IdP re-auth flow instead of showing an unusable password
   * modal. Evaluated lazily at 403 time so session state is loaded.
   */
  isFederatedUser?: () => boolean;
}

interface StepUpBody {
  error: 'step_up_required';
  acr_values_required?: string;
  method?: 'sso' | 'local';
  idp_id?: string;
}

/**
 * Paths that ARE the step-up ceremony. A 403 `step_up_required` from one of
 * these is the ceremony's own answer, not a new challenge: intercepting it
 * would park a queue entry nothing can resolve, because a dialog is already
 * open and the provider ignores the second event. The caller (the dialog)
 * would then wait forever on its own request.
 */
const STEP_UP_CEREMONY_PATHS = ['/auth/step-up/verify', '/auth/step-up/webauthn/begin'] as const;

/**
 * Shared with the forbidden middleware, which excludes the same two paths for
 * its own reason: their 403s are already rendered inside the step-up dialog.
 * One copy, so the two middlewares cannot drift apart.
 */
export function isStepUpCeremonyRequest(request: Request): boolean {
  let path: string;
  try {
    path = new URL(request.url).pathname;
  } catch {
    path = request.url;
  }
  return STEP_UP_CEREMONY_PATHS.some((p) => path.endsWith(p));
}

async function isStepUpResponse(response: Response): Promise<StepUpBody | null> {
  if (response.status !== 403) return null;
  // Clone so the caller still gets to read the response body if we don't
  // recognize this as a step-up case.
  const clone = response.clone();
  try {
    const body = (await clone.json()) as unknown;
    if (
      body &&
      typeof body === 'object' &&
      (body as { error?: unknown }).error === 'step_up_required'
    ) {
      return body as StepUpBody;
    }
    return null;
  } catch {
    // Unparseable JSON => do NOT treat as step-up.
    return null;
  }
}

// Stash a clone of every outgoing Request keyed by openapi-fetch's per-request
// id so that we can retry it after the user re-authenticates. The original
// Request body is consumed once openapi-fetch fetches it; the clone is not.
//
// Defensive: each entry also has a timeout that evicts it after a generous
// budget (60s) in case neither `onResponse` nor `onError` fires for that id
// (e.g. the request was aborted by an upstream signal before the lifecycle
// ever returns through this middleware). 60s is far longer than any sane
// HTTP request budget, so it doesn't fight legitimate retries while still
// preventing unbounded growth of the map.
const REQUEST_CLONE_TTL_MS = 60_000;
interface RequestCloneEntry {
  request: Request;
  timer: ReturnType<typeof setTimeout>;
}
const requestClones = new Map<string, RequestCloneEntry>();

function clearClone(id: string): Request | undefined {
  const entry = requestClones.get(id);
  if (!entry) return undefined;
  clearTimeout(entry.timer);
  requestClones.delete(id);
  return entry.request;
}

/** Test-only helper. Not part of the public API surface. */
export function __getRequestCloneCountForTests(): number {
  return requestClones.size;
}

/**
 * openapi-fetch middleware that intercepts 403 step_up_required responses,
 * dispatches a `sentinel:step-up-required` CustomEvent on `window`, awaits
 * the matching queue entry (resolved by the StepUpProvider after the user
 * re-authenticates), and retries the original request once.
 *
 * If the response is not a step-up case, or the body cannot be JSON-parsed,
 * the original response is returned unchanged.
 */
export function createStepUpMiddleware(opts: StepUpMiddlewareOptions): Middleware {
  const { queue } = opts;
  const fetcher = opts.fetcher ?? fetch;
  // `replay` takes precedence so the consumer can re-run the full middleware
  // chain (CSRF, etc.) against the freshly rotated session. Falls back to a
  // raw fetch when not provided.
  const reIssue: (r: Request) => Promise<Response> =
    opts.replay ?? ((r: Request) => fetcher(r));
  const win =
    opts.win ??
    (typeof window !== 'undefined'
      ? (window as unknown as StepUpMiddlewareOptions['win'])
      : undefined);

  return {
    async onRequest({ request, id }) {
      // Stash a clone before openapi-fetch consumes the body.
      try {
        const clone = request.clone();
        // Defensive eviction in case onResponse/onError never fires (e.g.
        // upstream abort).
        const timer = setTimeout(() => {
          requestClones.delete(id);
        }, REQUEST_CLONE_TTL_MS);
        // Best-effort: don't keep the Node event loop alive solely for this
        // timer (no-op in browsers / happy-dom).
        (timer as unknown as { unref?: () => void }).unref?.();
        requestClones.set(id, { request: clone, timer });
      } catch {
        // Some non-cloneable requests (rare) — skip. Retry will fail loudly.
      }
      return undefined;
    },
    async onResponse({ request, response, id }) {
      if (isStepUpCeremonyRequest(request)) {
        clearClone(id);
        return undefined;
      }
      const stepUp = await isStepUpResponse(response);
      if (!stepUp) {
        clearClone(id);
        return undefined;
      }
      if (!win) {
        // No window to dispatch the event on. Cannot trigger the dialog;
        // surface the original 403 unchanged.
        clearClone(id);
        return undefined;
      }

      // Federated (SSO) step-up: persist the blocked request and dispatch a
      // confirmation event. A FederatedStepUpProvider shows a prompt and, on
      // confirm, navigates to the IdP re-auth initiate endpoint. We do NOT
      // redirect here — the user must confirm first.
      if (stepUp.method === 'sso' || opts.isFederatedUser?.()) {
        const replayReq = clearClone(id);
        const loc = win.location;
        const returnTo = loc ? `${loc.pathname}${loc.search ?? ''}` : '/';
        try {
          if (replayReq) await persistPendingRequest(replayReq);
          const ev = new win.CustomEvent(STEP_UP_FEDERATED_EVENT, {
            detail: { returnTo },
          });
          win.dispatchEvent(ev);
        } catch {
          // Persist or dispatch failed (e.g. sessionStorage unavailable) —
          // fall through and surface the original 403.
        }
        return undefined;
      }

      const acr = stepUp.acr_values_required ?? '';

      // Take the clone out of the map now: its eviction timer is for a
      // request that never comes back, and the dialog can take longer than
      // that (an MFA code, a security key), which would otherwise drop the
      // replay and hand the page the original 403.
      const replayReq = clearClone(id);

      // Park the request on the queue. Thread the per-request acr through
      // so the consumer (StepUpProvider) can read the right value for this
      // entry instead of relying on whatever was active last.
      const entry = queue.enqueue({ acr });

      // Dispatch the event so a StepUpProvider can react.
      try {
        const ev = new win.CustomEvent('sentinel:step-up-required', {
          detail: { id: entry.id, acr },
        });
        win.dispatchEvent(ev);
      } catch {
        // If we can't dispatch, fail the queue entry to avoid hanging callers.
        clearClone(id);
        return undefined;
      }

      try {
        await entry.promise;
      } catch {
        // User cancelled (or queue rejected). Return the original 403.
        clearClone(id);
        return undefined;
      }

      // Retry the original request once, re-running the full middleware
      // chain so any rotated CSRF / session state is re-applied.
      if (!replayReq) return undefined;
      let retried: Response;
      try {
        retried = await reIssue(replayReq);
      } catch {
        // Network / fetch threw on retry. Don't return undefined — that
        // would surface the original 403 to the caller, which is misleading.
        // Synthesize a 502 so the caller sees a distinct, retry-specific
        // failure.
        return new Response(JSON.stringify({ error: 'step_up_retry_failed' }), {
          status: 502,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      // Loop fast-fail: if the retried request asks for step-up again (which
      // would normally re-enter this middleware), short-circuit with a 502
      // so callers do not loop indefinitely on broken step-up flows. Any other
      // 403 (the step-up worked but the request is still refused, such as a
      // resource that belongs to another account) is returned as it is, so
      // the caller sees the real reason.
      if (await isStepUpResponse(retried)) {
        return new Response(
          JSON.stringify({ error: 'step_up_retry_required_again' }),
          { status: 502, headers: { 'Content-Type': 'application/json' } },
        );
      }
      return retried;
    },
    async onError({ id }) {
      clearClone(id);
      return undefined;
    },
  };
}
